import React, { useState, useEffect } from 'react';
import { FaSchool, FaSearch, FaStar, FaMapMarkerAlt, FaHeart, FaRegHeart, FaFilter, FaTimes } from 'react-icons/fa';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import LoadingSpinner from '../common/LoadingSpinner';
import Modal from '../common/Modal';
import { useSubscription } from '../../hooks/useSubscription';

/**
 * School search component for parents
 * Allows searching, filtering, and viewing school details
 */
const SchoolSearch = () => {
  const { showSuccess, showError } = useToast();
  const { hasTier } = useSubscription();

  // Schools state
  const [schools, setSchools] = useState([]);
  const [filteredSchools, setFilteredSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [savedSchools, setSavedSchools] = useState([]);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    curriculum: [],
    location: '',
    rating: 0,
    fees: {
      min: '',
      max: '',
    },
    boardingOptions: [],
  });

  // Fetch schools on component mount
  useEffect(() => {
    fetchSchools();
    fetchSavedSchools();
  }, []);

  // Apply search and filters when they change
  useEffect(() => {
    applyFilters();
  }, [schools, searchTerm, filters]);

  // Fetch schools from API
  const fetchSchools = async () => {
    try {
      setLoading(true);
      const response = await api.get('/schools');
      setSchools(response.data.data || []);
      setFilteredSchools(response.data.data || []);
    } catch (error) {
      console.error('Error fetching schools:', error);
      showError('Failed to load schools');
    } finally {
      setLoading(false);
    }
  };

  // Fetch user's saved schools
  const fetchSavedSchools = async () => {
    // Temporarily disabled due to missing backend endpoint
    /*
    try {
      const response = await api.get('/parents/saved-schools');
      setSavedSchools(response.data.data.map(school => school._id) || []);
    } catch (error) {
      console.error('Error fetching saved schools:', error);
    }
    */
    // Set to empty array for now
    setSavedSchools([]);
  };

  // Apply search and filters
  const applyFilters = () => {
    let result = [...schools];
    
    // Apply search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(
        school => 
          school.name.toLowerCase().includes(search) ||
          (school.location?.county && school.location.county.toLowerCase().includes(search)) ||
          (school.location?.town && school.location.town.toLowerCase().includes(search))
      );
    }
    
    // Apply curriculum filter
    if (filters.curriculum.length > 0) {
      result = result.filter(school => {
        if (!school.curriculum) return false;
        
        if (Array.isArray(school.curriculum)) {
          return filters.curriculum.some(curr => school.curriculum.includes(curr));
        } else {
          // If curriculum is a string, check if it matches any of the selected curricula
          return filters.curriculum.includes(school.curriculum);
        }
      });
    }
    
    // Apply location filter
    if (filters.location) {
      const location = filters.location.toLowerCase();
      result = result.filter(
        school => 
          (school.location?.county && school.location.county.toLowerCase().includes(location)) ||
          (school.location?.town && school.location.town.toLowerCase().includes(location))
      );
    }
    
    // Apply rating filter
    if (filters.rating > 0) {
      result = result.filter(school => school.rating >= filters.rating);
    }
    
    // Apply fees filter
    if (filters.fees.min) {
      result = result.filter(school => 
        school.fees?.termly && school.fees.termly >= parseFloat(filters.fees.min)
      );
    }
    
    if (filters.fees.max) {
      result = result.filter(school => 
        school.fees?.termly && school.fees.termly <= parseFloat(filters.fees.max)
      );
    }
    
    // Apply boarding options filter
    if (filters.boardingOptions.length > 0) {
      result = result.filter(school => {
        if (filters.boardingOptions.includes('boarding') && school.hasBoarding) {
          return true;
        }
        if (filters.boardingOptions.includes('day') && school.hasDaySchool) {
          return true;
        }
        return false;
      });
    }
    
    setFilteredSchools(result);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle filter change
  const handleFilterChange = (filterType, value) => {
    setFilters(prevFilters => {
      if (filterType === 'curriculum' || filterType === 'boardingOptions') {
        // For array filters, toggle the value
        const currentValues = [...prevFilters[filterType]];
        const index = currentValues.indexOf(value);
        
        if (index === -1) {
          currentValues.push(value);
        } else {
          currentValues.splice(index, 1);
        }
        
        return { ...prevFilters, [filterType]: currentValues };
      } else if (filterType === 'fees') {
        // For fees object, update specific property
        return { 
          ...prevFilters, 
          fees: { ...prevFilters.fees, ...value }
        };
      } else {
        // For other filters, just set the value
        return { ...prevFilters, [filterType]: value };
      }
    });
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setFilters({
      curriculum: [],
      location: '',
      rating: 0,
      fees: {
        min: '',
        max: '',
      },
      boardingOptions: [],
    });
  };

  // Toggle saving a school
  const toggleSaveSchool = async (schoolId) => {
    // Temporarily disabled due to missing backend endpoint
    /*
    try {
      if (savedSchools.includes(schoolId)) {
        // Unsave the school
        await api.delete(`/parents/saved-schools/${schoolId}`);
        setSavedSchools(savedSchools.filter(id => id !== schoolId));
        showSuccess('School removed from favorites');
      } else {
        // Save the school
        await api.post('/parents/saved-schools', { schoolId });
        setSavedSchools([...savedSchools, schoolId]);
        showSuccess('School added to favorites');
      }
    } catch (error) {
      console.error('Error toggling saved school:', error);
      showError('Failed to update favorites');
    }
    */
    showError('Saving schools feature coming soon');
  };

  // View school details
  const viewSchoolDetails = (school) => {
    setSelectedSchool(school);
    setShowDetailsModal(true);
  };

  // Render star ratings
  const renderRatingStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <FaStar key={i} className={i <= rating ? "text-yellow-500" : "text-gray-300"} />
      );
    }
    return <div className="flex space-x-1">{stars}</div>;
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <FaSchool className="mr-2 text-primary" /> School Search
          </h1>
          <p className="text-gray-600 mt-1">
            Find and compare schools for your children
          </p>
        </div>
      </div>
      
      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-soft p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          {/* Search Bar */}
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="text-gray-400" />
            </div>
            <input
              type="text"
              className="form-input pl-10 py-2 w-full"
              placeholder="Search schools by name or location..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
          
          {/* Filter Toggle */}
          <div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center justify-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md w-full lg:w-auto"
            >
              <FaFilter className="mr-2" /> 
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
          </div>
        </div>
        
        {/* Expanded Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Curriculum Filter */}
              <div>
                <h4 className="font-medium mb-2">Curriculum</h4>
                <div className="space-y-2">
                  {['8-4-4', 'CBC', 'IGCSE', 'IB'].map(curr => (
                    <label key={curr} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={filters.curriculum.includes(curr)}
                        onChange={() => handleFilterChange('curriculum', curr)}
                        className="form-checkbox mr-2"
                      />
                      <span>{curr}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Location Filter */}
              <div>
                <h4 className="font-medium mb-2">Location</h4>
                <input
                  type="text"
                  value={filters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                  className="form-input w-full"
                  placeholder="County or town..."
                />
              </div>
              
              {/* Rating Filter */}
              <div>
                <h4 className="font-medium mb-2">Minimum Rating</h4>
                <div className="flex items-center space-x-2">
                  <input
                    type="range"
                    min="0"
                    max="5"
                    step="1"
                    value={filters.rating}
                    onChange={(e) => handleFilterChange('rating', parseInt(e.target.value))}
                    className="form-range w-full"
                  />
                  <span>{filters.rating === 0 ? 'Any' : `${filters.rating}+`}</span>
                </div>
              </div>
              
              {/* Fees Filter */}
              <div>
                <h4 className="font-medium mb-2">Fees Range (KES)</h4>
                <div className="flex space-x-2">
                  <input
                    type="number"
                    value={filters.fees.min}
                    onChange={(e) => handleFilterChange('fees', { min: e.target.value })}
                    className="form-input w-full"
                    placeholder="Min"
                  />
                  <span>-</span>
                  <input
                    type="number"
                    value={filters.fees.max}
                    onChange={(e) => handleFilterChange('fees', { max: e.target.value })}
                    className="form-input w-full"
                    placeholder="Max"
                  />
                </div>
              </div>
              
              {/* Boarding Options */}
              <div>
                <h4 className="font-medium mb-2">School Type</h4>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.boardingOptions.includes('boarding')}
                      onChange={() => handleFilterChange('boardingOptions', 'boarding')}
                      className="form-checkbox mr-2"
                    />
                    <span>Boarding School</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.boardingOptions.includes('day')}
                      onChange={() => handleFilterChange('boardingOptions', 'day')}
                      className="form-checkbox mr-2"
                    />
                    <span>Day School</span>
                  </label>
                </div>
              </div>
              
              {/* Advanced filters (only for paid tiers) */}
              <div>
                <h4 className="font-medium mb-2">Advanced Filters</h4>
                {hasTier(['essential', 'growth', 'plus']) ? (
                  <p className="text-sm text-gray-500">
                    Additional filters available with your plan
                  </p>
                ) : (
                  <p className="text-sm text-primary">
                    Upgrade your plan to access advanced filters
                  </p>
                )}
              </div>
            </div>
            
            <div className="mt-4 flex justify-end">
              <button
                onClick={resetFilters}
                className="flex items-center px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                <FaTimes className="mr-1" /> Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Schools List */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-soft p-8 flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-soft">
          {filteredSchools.length === 0 ? (
            <div className="p-8 text-center">
              <FaSchool className="mx-auto text-gray-300 text-5xl mb-4" />
              <h3 className="text-xl font-medium text-gray-600 mb-2">No Schools Found</h3>
              <p className="text-gray-500 mb-4">Try adjusting your search criteria or filters</p>
              <button
                onClick={resetFilters}
                className="btn-primary"
              >
                Reset Filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
              {filteredSchools.map((school) => (
                <div 
                  key={school._id} 
                  className="border border-gray-200 rounded-lg hover:border-primary transition-colors overflow-hidden"
                >
                  <div className="relative h-48 overflow-hidden">
                    {school.photos && school.photos.length > 0 ? (
                      <img 
                        src={school.photos[0]} 
                        alt={school.name} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                        <FaSchool className="text-gray-400 text-4xl" />
                      </div>
                    )}
                    <button
                      onClick={() => toggleSaveSchool(school._id)}
                      className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-md"
                      title={savedSchools.includes(school._id) ? "Remove from favorites" : "Add to favorites"}
                    >
                      {savedSchools.includes(school._id) ? (
                        <FaHeart className="text-red-500" />
                      ) : (
                        <FaRegHeart className="text-gray-500" />
                      )}
                    </button>
                  </div>
                  
                  <div className="p-4">
                    <h3 className="font-semibold text-lg mb-1">{school.name}</h3>
                    <div className="flex items-center text-sm text-gray-500 mb-2">
                      <FaMapMarkerAlt className="mr-1" />
                      <span>
                        {school.location?.county || 'Location'}{school.location?.town ? `, ${school.location.town}` : ''}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center mb-3">
                      <div className="flex items-center">
                        {renderRatingStars(school.rating || 0)}
                        <span className="ml-1 text-sm text-gray-500">({school.reviewCount || 0})</span>
                      </div>
                      {school.fees?.termly && (
                        <div className="text-sm font-medium">
                          KES {school.fees.termly.toLocaleString()} / term
                        </div>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {school.curriculum && Array.isArray(school.curriculum) ? (
                        school.curriculum.map((curr, idx) => (
                          <span 
                            key={idx} 
                            className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                          >
                            {curr}
                          </span>
                        ))
                      ) : school.curriculum ? (
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                          {school.curriculum}
                        </span>
                      ) : null}
                      {school.hasBoarding && (
                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                          Boarding
                        </span>
                      )}
                      {school.hasDaySchool && (
                        <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                          Day School
                        </span>
                      )}
                    </div>
                    
                    <button
                      onClick={() => viewSchoolDetails(school)}
                      className="w-full btn-primary-outline text-sm py-2"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* School Details Modal */}
      {selectedSchool && (
        <Modal
          isOpen={showDetailsModal}
          onClose={() => setShowDetailsModal(false)}
          title={selectedSchool.name}
          size="xl"
        >
          <div className="p-6">
            {/* School Photos */}
            {selectedSchool.photos && selectedSchool.photos.length > 0 ? (
              <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-2">
                {selectedSchool.photos.slice(0, 3).map((photo, idx) => (
                  <img 
                    key={idx} 
                    src={photo} 
                    alt={`${selectedSchool.name} - Photo ${idx + 1}`} 
                    className="rounded-lg h-48 w-full object-cover"
                  />
                ))}
              </div>
            ) : (
              <div className="mb-6 h-48 bg-gray-200 flex items-center justify-center rounded-lg">
                <FaSchool className="text-gray-400 text-4xl" />
              </div>
            )}
            
            {/* School Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="col-span-2">
                <h3 className="font-semibold text-lg mb-4">About {selectedSchool.name}</h3>
                <p className="text-gray-700 mb-6">
                  {selectedSchool.description || "No description available."}
                </p>
                
                <h4 className="font-semibold mb-2">Key Information</h4>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <p className="text-sm">
                      <span className="font-medium">Curriculum:</span>{' '}
                      {Array.isArray(selectedSchool.curriculum) 
                        ? selectedSchool.curriculum.map((curr, idx) => (
                            <span key={idx}>{typeof curr === 'object' ? JSON.stringify(curr) : curr}{idx < selectedSchool.curriculum.length - 1 ? ', ' : ''}</span>
                          ))
                        : (typeof selectedSchool.curriculum === 'object' ? JSON.stringify(selectedSchool.curriculum) : (selectedSchool.curriculum || 'N/A'))}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">Location:</span>{' '}
                      {typeof selectedSchool.location === 'object' && selectedSchool.location !== null ? (
                        <>
                          {selectedSchool.location.county || 'N/A'}
                          {selectedSchool.location.town ? `, ${selectedSchool.location.town}` : ''}
                        </>
                      ) : (
                        'N/A'
                      )}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">School Type:</span>{' '}
                      {[
                        selectedSchool.hasBoarding ? 'Boarding' : null,
                        selectedSchool.hasDaySchool ? 'Day School' : null
                      ].filter(Boolean).join(', ') || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm">
                      <span className="font-medium">Founded:</span>{' '}
                      {selectedSchool.foundedYear || 'N/A'}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">Student Population:</span>{' '}
                      {selectedSchool.studentPopulation ? selectedSchool.studentPopulation.toLocaleString() : 'N/A'}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">Class Size:</span>{' '}
                      {selectedSchool.averageClassSize || 'N/A'}
                    </p>
                  </div>
                </div>
                
                <h4 className="font-semibold mb-2">Facilities</h4>
                <div className="flex flex-wrap gap-2 mb-6">
                  {selectedSchool.facilities && selectedSchool.facilities.length > 0 ? (
                    selectedSchool.facilities.map((facility, idx) => (
                      <span 
                        key={idx} 
                        className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full"
                      >
                        {typeof facility === 'object' ? JSON.stringify(facility) : facility}
                      </span>
                    ))
                  ) : (
                    <p className="text-gray-500">No facility information available</p>
                  )}
                </div>
                
                <h4 className="font-semibold mb-2">Programs & Activities</h4>
                <div className="mb-6">
                  {selectedSchool.programs ? (
                    <p className="text-sm text-gray-700">{selectedSchool.programs}</p>
                  ) : (
                    <p className="text-gray-500">No program information available</p>
                  )}
                </div>
              </div>
              
              <div>
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <h4 className="font-semibold mb-2">Fees</h4>
                  {selectedSchool.fees?.termly ? (
                    <div>
                      <p className="text-sm">
                        <span className="font-medium">Termly:</span>{' '}
                        KES {selectedSchool.fees.termly.toLocaleString()}
                      </p>
                      {selectedSchool.fees.annualTotal && (
                        <p className="text-sm">
                          <span className="font-medium">Annual Total:</span>{' '}
                          KES {selectedSchool.fees.annualTotal.toLocaleString()}
                        </p>
                      )}
                      {selectedSchool.fees.admissionFee && (
                        <p className="text-sm">
                          <span className="font-medium">Admission Fee:</span>{' '}
                          KES {selectedSchool.fees.admissionFee.toLocaleString()}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-gray-500">Fee information not available</p>
                  )}
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <h4 className="font-semibold mb-2">Ratings & Reviews</h4>
                  <div className="flex items-center mb-2">
                    {renderRatingStars(selectedSchool.rating || 0)}
                    <span className="ml-2 font-medium">{selectedSchool.rating || 0}/5</span>
                  </div>
                  <p className="text-sm text-gray-500">
                    Based on {selectedSchool.reviewCount || 0} parent reviews
                  </p>
                  
                  {hasTier(['growth', 'plus']) ? (
                    <button
                      className="w-full mt-3 text-sm text-primary font-medium"
                    >
                      View Detailed Reviews
                    </button>
                  ) : (
                    <p className="text-xs text-primary mt-3">
                      Upgrade to Growth plan to see detailed reviews
                    </p>
                  )}
                </div>
                
                <div className="mt-4">
                  <button
                    onClick={() => toggleSaveSchool(selectedSchool._id)}
                    className={`w-full btn ${savedSchools.includes(selectedSchool._id) 
                      ? 'bg-red-500 hover:bg-red-600 text-white' 
                      : 'btn-primary'}`}
                  >
                    {savedSchools.includes(selectedSchool._id)
                      ? 'Remove from Favorites'
                      : 'Add to Favorites'}
                  </button>
                  
                  {selectedSchool.website && (
                    <a
                      href={selectedSchool.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full btn-secondary flex justify-center items-center mt-2"
                    >
                      Visit School Website
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default SchoolSearch;
